/**
 * Organization Context
 * 
 * Manages the current organization selection and provides org data to the app.
 */

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getMyOrganizations, acceptPendingOrgInvites, type Organization } from '@/db/organizations';
import { handleError } from '@/utils/errorHandler';

interface OrganizationContextType {
  organizations: Organization[];
  currentOrg: Organization | null;
  setCurrentOrg: (org: Organization | null) => void;
  loading: boolean;
  refreshOrgs: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

const CURRENT_ORG_KEY = 'zenda-current-org-id';
const CURRENT_ORG_DATA_KEY = 'zenda-current-org-data';

function getCachedOrg(): Organization | null {
  try {
    const data = localStorage.getItem(CURRENT_ORG_DATA_KEY);
    if (data) return JSON.parse(data) as Organization;
  } catch { /* ignore parse errors */ }
  return null;
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  // Initialize from cached org data for instant rendering (prevents layout shift)
  const [currentOrg, setCurrentOrgState] = useState<Organization | null>(getCachedOrg);
  const [loading, setLoading] = useState(true);

  const loadOrgs = useCallback(async () => {
    if (!user) {
      setOrganizations([]);
      setCurrentOrgState(null);
      localStorage.removeItem(CURRENT_ORG_DATA_KEY);
      setLoading(false);
      return;
    }

    try {
      // Accept any pending org invites before loading orgs
      // so the user sees all their organizations immediately after signup
      try {
        await acceptPendingOrgInvites();
      } catch (e) {
        handleError(e, { level: 'warning', message: 'Errore nell\'accettazione degli inviti pending' });
      }

      const orgs = await getMyOrganizations();
      setOrganizations(orgs);

      // Restore last selected org
      const savedOrgId = localStorage.getItem(CURRENT_ORG_KEY);
      const savedOrg = savedOrgId ? orgs.find(o => o.id === savedOrgId) : null;
      
      if (savedOrg) {
        setCurrentOrgState(savedOrg);
        localStorage.setItem(CURRENT_ORG_DATA_KEY, JSON.stringify(savedOrg));
      } else if (orgs.length > 0) {
        setCurrentOrgState(orgs[0]);
        localStorage.setItem(CURRENT_ORG_KEY, orgs[0].id);
        localStorage.setItem(CURRENT_ORG_DATA_KEY, JSON.stringify(orgs[0]));
      }
    } catch (error) {
      handleError(error, { level: 'error', message: 'Errore nel caricamento delle organizzazioni' });
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  const setCurrentOrg = useCallback((org: Organization | null) => {
    setCurrentOrgState(org);
    if (org) {
      localStorage.setItem(CURRENT_ORG_KEY, org.id);
      localStorage.setItem(CURRENT_ORG_DATA_KEY, JSON.stringify(org));
    } else {
      localStorage.removeItem(CURRENT_ORG_KEY);
      localStorage.removeItem(CURRENT_ORG_DATA_KEY);
    }
  }, []);

  const refreshOrgs = useCallback(async () => {
    if (!user) return;
    try {
      const orgs = await getMyOrganizations();
      setOrganizations(orgs);

      // If new orgs appeared (e.g. after accepting invite), auto-select the new one
      const currentOrgStillExists = currentOrg && orgs.find(o => o.id === currentOrg.id);
      if (!currentOrgStillExists && orgs.length > 0) {
        // Find the org that wasn't in the previous list (the newly joined one)
        const prevIds = new Set(organizations.map(o => o.id));
        const newOrg = orgs.find(o => !prevIds.has(o.id));
        const orgToSelect = newOrg || orgs[0];
        setCurrentOrgState(orgToSelect);
        localStorage.setItem(CURRENT_ORG_KEY, orgToSelect.id);
        localStorage.setItem(CURRENT_ORG_DATA_KEY, JSON.stringify(orgToSelect));
      }
    } catch (error) {
      handleError(error, { level: 'error', message: 'Errore nel caricamento delle organizzazioni' });
    }
  }, [user, currentOrg, organizations]);

  return (
    <OrganizationContext.Provider value={{ organizations, currentOrg, setCurrentOrg, loading, refreshOrgs }}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}
